/**
 * Story event handlers — character, scene, beat CRUD + image updates.
 */

import type { Socket } from "socket.io-client";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
} from "@amzn/a4k-creator-studio-shared";
import { useStoryStore } from "../storyStore";
import { useComicStore } from "../comicStore";
import { setCachedImage } from "../../lib/imageCache";
import { trackError, trackModeration } from "../../services/metrics";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export function registerStoryHandlers(socket: TypedSocket): void {
  socket.on("storyCreated", (data) => {
    useStoryStore.getState().setStory(data.storyId, data.title);
  });

  socket.on("storyCleared", () => {
    useStoryStore.getState().clearAll();
    useComicStore.getState().clearAll();
  });

  socket.on("storyCharacterAdded", (data) => {
    useStoryStore.getState().addCharacter({
      characterId: data.characterId,
      name: data.name,
      visualDescription: data.visualDescription,
      personality: data.personality,
      isLoading: true,
    });
    // Adding a character keeps the grid view — focus is driven only by the AI
    // actively referencing a character (toolUse) or a manual tap.
  });

  socket.on("storyCharacterUpdated", (data) => {
    useStoryStore.getState().updateCharacter(data.characterId, {
      name: data.name,
      visualDescription: data.visualDescription,
      personality: data.personality,
    });
  });

  socket.on("storyCharacterRemoved", (data) => {
    useStoryStore.getState().removeCharacter(data.characterId);
  });

  socket.on("storyCharacterImageUpdated", (data) => {
    useStoryStore.getState().updateCharacter(data.characterId, {
      ...(data.imageBase64 !== undefined && { imageBase64: data.imageBase64, imageId: data.imageId }),
      isLoading: false,
      hasError: false,
      isModerated: false,
    });
    if (data.imageId && data.imageBase64) {
      setCachedImage(data.imageId, data.imageBase64).catch(() => {});
    }
  });

  // Track which character the AI is talking about via tool calls
  socket.on("toolUse", (data) => {
    try {
      if (!data.content) return;
      const input = typeof data.content === "string" ? JSON.parse(data.content) : data.content;
      const charId = input?.characterId;
      if (charId) useStoryStore.getState().setVoiceFocus(charId);
    } catch { /* ignore parse errors */ }
  });

  socket.on("storySceneAdded", (data) => {
    useStoryStore.getState().addScene({
      sceneId: data.sceneId,
      name: data.name,
      visualDescription: data.visualDescription,
      isLoading: true,
    });
  });

  socket.on("storySceneUpdated", (data) => {
    useStoryStore.getState().updateScene(data.sceneId, {
      name: data.name,
      visualDescription: data.visualDescription,
    });
  });

  socket.on("storySceneRemoved", (data) => {
    useStoryStore.getState().removeScene(data.sceneId);
  });

  socket.on("storySceneImageUpdated", (data) => {
    useStoryStore.getState().updateScene(data.sceneId, {
      ...(data.imageBase64 !== undefined && { imageBase64: data.imageBase64, imageId: data.imageId }),
      isLoading: false,
      hasError: false,
      isModerated: false,
    });
    if (data.imageId && data.imageBase64) {
      setCachedImage(data.imageId, data.imageBase64).catch(() => {});
    }
  });

  socket.on("storyImageLoading", (data) => {
    if (data.type === "character") {
      useStoryStore.getState().updateCharacter(data.id, { isLoading: true, isModerated: false });
    } else if (data.type === "beat") {
      useStoryStore.getState().updateBeat(data.id, { imageLoading: true, isModerated: false });
    } else {
      useStoryStore.getState().updateScene(data.id, { isLoading: true, isModerated: false });
    }
  });

  socket.on("storyImageError", (data) => {
    trackError("Story", data.elementType === "character" ? "character" : "scene");
    if (data.elementType === "character") {
      useStoryStore.getState().updateCharacter(data.id, { isLoading: false, hasError: true });
    } else {
      useStoryStore.getState().updateScene(data.id, { isLoading: false, hasError: true });
    }
  });

  socket.on("storyBeatAdded", (data) => {
    useStoryStore.getState().addBeat({
      beatId: data.beatId,
      order: data.order,
      characterIds: data.characterIds,
      sceneId: data.sceneId,
      action: data.action,
      visualDescription: data.visualDescription,
      beatType: data.beatType,
      imageLoading: true,
    });
  });

  socket.on("storyBeatUpdated", (data) => {
    useStoryStore.getState().updateBeat(data.beatId, {
      order: data.order,
      characterIds: data.characterIds,
      sceneId: data.sceneId,
      action: data.action,
      visualDescription: data.visualDescription,
      beatType: data.beatType,
    });
  });

  socket.on("storyBeatRemoved", (data) => {
    useStoryStore.getState().removeBeat(data.beatId);
  });

  socket.on("storyBeatImageUpdated", (data) => {
    useStoryStore.getState().updateBeat(data.beatId, {
      ...(data.imageBase64 !== undefined && { imageBase64: data.imageBase64, imageId: data.imageId }),
      imageLoading: false,
      isModerated: false,
    });
    if (data.imageId && data.imageBase64) {
      setCachedImage(data.imageId, data.imageBase64).catch(() => {});
    }
  });

  socket.on("storyBeatImageError", (data) => {
    trackError("Story", "beat");
    useStoryStore.getState().updateBeat(data.beatId, { imageLoading: false });
  });

  // Image moderation: mark element with red border. Server will emit removal after 5s.
  socket.on("moderationBlocked", (data) => {
    trackModeration("Story", data.type);
    const store = useStoryStore.getState();
    if (data.type === "character") {
      store.updateCharacter(data.id, { isLoading: false, isModerated: true });
    } else if (data.type === "scene") {
      store.updateScene(data.id, { isLoading: false, isModerated: true });
    } else if (data.type === "beat") {
      store.updateBeat(data.id, { imageLoading: false, isModerated: true });
    }
  });
}
